import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  GlobalNavigationProps,
  HeaderCtaButtonLink,
  HeaderCtaButtons,
  HeaderNavigationDropdown,
  HeaderNavigationLink,
  HeaderProps,
  NAV_ITEM_COLOR,
  UtilityNavigationMobileProps,
} from '@ahc-digital/component-library';
import {
  N01HeaderProps,
  N01MainGlobalNavigationItem,
  N01MainGlobalNavigationSection,
  N02UtilityNavigationItem,
  N02UtilityNavigationLanguageSelectorItem,
  N02UtilityNavigationSection,
} from './N01Header.types';
import {
  N01_GLOBAL_NAV_SECTION_TEMPLATE_ID,
  N02_UTILITY_NAV_SECTION_TEMPLATE_ID,
} from 'src/shared/static';
import { useI18n } from 'next-localization';
import { parseDroplinkParam } from 'src/shared/shared.service';

/**
 * @todo
 * - 'append-path' is no longer needed since the language selector will take its place
 * - the language selector needs to factor in the current path / query params when clicked
 */

type PageLanguage = 'en' | 'es';

export function GetHeaderModel(props: N01HeaderProps | undefined, searchPageUrl: string) {
  const id = props?.params?.RenderingIdentifier;
  const backgroundColor =
    props?.params?.BackgroundColor &&
    parseDroplinkParam(props?.params?.BackgroundColor).value?.value;
  const firstButtonColor =
    props?.params?.FirstButtonColor &&
    parseDroplinkParam(props?.params?.FirstButtonColor).value?.value;
  const secondButtonColor =
    props?.params?.SecondButtonColor &&
    parseDroplinkParam(props?.params?.SecondButtonColor).value?.value;
  const { t } = useI18n() || {};
  const closeText = t('Nav-Close');
  const MenuText = t('Nav-Menu');
  const searchText = t('Nav-Search');
  const logoText = t('Nav-Logo');
  const searchPlaceholderText = t('Search-Placeholder-text');
  const { mainNavigation, utilityNavigation } = props?.fields?.data?.header ?? {};

  // get window.location information for Espanol link
  const pathName = usePathname();
  const [currentPageLocation, setCurrentPageLocation] = useState<Location>({} as Location);
  const [currentLang, setCurrentLang] = useState<PageLanguage>('en');

  useEffect(() => {
    setCurrentPageLocation(window.location);
    setCurrentLang(document.documentElement.lang as 'en' | 'es');
  }, [pathName]);

  const navItems = BuildNavigationItems(mainNavigation?.targetItem?.children?.results);
  const utilityNavItems = BuildUtilityNavigationItems(
    utilityNavigation?.targetItem?.children?.results,
    currentPageLocation,
    currentLang
  );
  const ctaButtonsList = [];
  if (utilityNavigation?.targetItem?.cTAButtonLink?.jsonValue?.value?.href !== '') {
    ctaButtonsList?.push({
      link: (
        <a
          href={utilityNavigation?.targetItem?.cTAButtonLink?.jsonValue?.value?.href}
          target={utilityNavigation?.targetItem?.cTAButtonLink?.jsonValue?.value?.target}
        >
          {utilityNavigation?.targetItem?.cTAButtonLink?.text}
        </a>
      ),
      color: firstButtonColor,
    } as HeaderCtaButtonLink);
  }
  if (utilityNavigation?.targetItem?.cTAButtonLink2?.jsonValue?.value?.href !== '') {
    ctaButtonsList?.push({
      link: (
        <a
          href={utilityNavigation?.targetItem?.cTAButtonLink2?.jsonValue?.value?.href}
          target={utilityNavigation?.targetItem?.cTAButtonLink2?.jsonValue?.value?.target}
        >
          {utilityNavigation?.targetItem?.cTAButtonLink2?.text}
        </a>
      ),
      color: secondButtonColor,
    } as HeaderCtaButtonLink);
  }

  const globalNavigation = {
    logoUrl: mainNavigation?.targetItem?.logoUrl?.jsonValue?.value?.href || '',
    logoImage: (
      <img
        src={mainNavigation?.targetItem?.logo?.jsonValue?.value?.src}
        alt={mainNavigation?.targetItem?.logo?.jsonValue?.value?.alt}
        width={mainNavigation?.targetItem?.logo?.width ?? ''}
        height={mainNavigation?.targetItem?.logo?.height ?? ''}
      />
    ),
    logoImageInverse: (
      <img
        src={mainNavigation?.targetItem?.logoImageInverse?.jsonValue?.value?.src}
        alt={mainNavigation?.targetItem?.logoImageInverse?.jsonValue?.value?.alt}
        width={mainNavigation?.targetItem?.logoImageInverse?.width ?? ''}
        height={mainNavigation?.targetItem?.logoImageInverse?.height ?? ''}
      />
    ),
    logoTitle: mainNavigation?.targetItem?.logoTitle?.value || '',
    backgroundColor: (backgroundColor as GlobalNavigationProps['backgroundColor']) || 'white',
    ariaLabel: 'Main Navigation',
    navigationItems: navItems,
    mobileMenuTriggerOpenLabel: MenuText,
    mobileMenuTriggerCloseLabel: closeText,
    searchLabel: searchText,
    logoLabel: logoText,
    searchPlaceholder: searchPlaceholderText,
    searchUrl: searchPageUrl,
    searchColor: mainNavigation?.targetItem?.searchBarColor?.targetItem?.value
      ?.value as NAV_ITEM_COLOR,
    mobileCallLink: !!mainNavigation?.targetItem?.mobileSupportLink?.jsonValue?.value?.href ? (
      <a
        href={mainNavigation?.targetItem?.mobileSupportLink?.jsonValue?.value?.href}
        target={mainNavigation?.targetItem?.mobileSupportLink?.jsonValue?.value?.target}
      >
        {mainNavigation?.targetItem?.mobileSupportLink?.jsonValue?.value?.text}
      </a>
    ) : undefined,
  };

  const utilNav = {
    ariaLabel: 'Utility Navigation',
    navigationItems: utilityNavItems,
    ctaButtons: ctaButtonsList as HeaderCtaButtons,
  };

  const featuredUtilityCtaNumber =
    utilityNavigation?.targetItem?.highlightedMobileCTA?.value !== 'neither'
      ? (parseInt(
          utilityNavigation?.targetItem?.highlightedMobileCTA?.value || ''
        ) as HeaderProps['featuredUtilityCtaNumber'])
      : undefined;

  return {
    id,
    globalNavigation,
    utilityNavigation: utilityNavigation?.targetItem ? utilNav : undefined,
    featuredUtilityCtaNumber,
  };
}

function BuildNavigationItems(
  data?: (N01MainGlobalNavigationItem | N01MainGlobalNavigationSection)[]
): GlobalNavigationProps['navigationItems'] {
  const navigationItems: GlobalNavigationProps['navigationItems'] = [];
  data?.map((child) => {
    // if child is of type N01-Global Navigation Section
    if (child?.template?.id === N01_GLOBAL_NAV_SECTION_TEMPLATE_ID) {
      navigationItems?.push({
        color: child?.coloredBarClass?.targetItem?.value?.value as NAV_ITEM_COLOR,
        ...BuildHeaderNavSection(child),
      });
      // if child is of type N01-Global Navigation Item
    } else {
      navigationItems?.push({
        color: child?.coloredBarClass?.targetItem?.value?.value as NAV_ITEM_COLOR,
        ...BuildHeaderNavLink(child),
      });
    }
  });

  return navigationItems;
}

function BuildUtilityNavigationItems(
  data?: (
    | N02UtilityNavigationItem
    | N02UtilityNavigationLanguageSelectorItem
    | N02UtilityNavigationSection
  )[],
  pageLocation?: Location,
  pageLanguage?: PageLanguage
): UtilityNavigationMobileProps['navigationItems'] {
  const navigationItems: UtilityNavigationMobileProps['navigationItems'] = [];
  data?.map((child) => {
    // if child is of type N02 - Utility Navigation Item Section
    if (child?.template?.id === N02_UTILITY_NAV_SECTION_TEMPLATE_ID) {
      navigationItems?.push(BuildHeaderNavSection(child, pageLocation));
    }
    // if child is of type N02 - Utility Navigatin Item
    else {
      navigationItems?.push(BuildHeaderNavLink(child, pageLocation, pageLanguage));
    }
  });
  return navigationItems;
}

function BuildHeaderNavSection(
  data: N02UtilityNavigationSection,
  pageLocation?: Location
): HeaderNavigationDropdown {
  return {
    type: 'dropdown',
    title: data?.title?.value || '',
    links: (data?.children?.results ?? []).map((item) => {
      return BuildHeaderNavLink(item, pageLocation);
    }),
  };
}

function BuildHeaderNavLink(
  data:
    | N01MainGlobalNavigationItem
    | N02UtilityNavigationItem
    | N02UtilityNavigationLanguageSelectorItem,
  pageLocation?: Location,
  pageLanguage?: PageLanguage
): HeaderNavigationLink {
  // if "spanishLink" exists, then this is the N02UtilityNavigationLanguageSelectorItem type
  if ('spanishLink' in data)
    return CreateHeaderNavigationLanguageSelectorLink(data, pageLocation, pageLanguage);

  return CreateHeaderNavigationLink(
    data?.link?.jsonValue?.value?.href || '',
    data?.link?.jsonValue?.value?.target || '',
    data?.link?.text || '',
    data?.mobileText?.value || ''
  );
}

function CreateHeaderNavigationLanguageSelectorLink(
  data: N02UtilityNavigationLanguageSelectorItem,
  pageLocation?: Location,
  pageLanguage?: PageLanguage
): HeaderNavigationLink {
  let url = data?.link?.jsonValue?.value?.href || '';
  let target = data?.link?.jsonValue?.value?.target || '';
  let linkText = data?.link?.text || '';
  let mobileText = data?.mobileText?.value || '';

  // if the current site is spanish, use the Spanish specific fields
  if (pageLanguage == 'es') {
    url = data?.spanishLink?.jsonValue?.value?.href || url;
    target = data?.spanishLink?.jsonValue?.value?.target || target;
    linkText = data?.spanishLink?.text || linkText;
    mobileText = data?.spanishMobileText?.value || mobileText;
  }

  //make sure the current path and params are appended to the Espanol link
  url = url.endsWith('/') ? url.substring(0, url.length - 1) : url;
  url += pageLocation?.pathname ?? '' + pageLocation?.search;

  return CreateHeaderNavigationLink(url, target, linkText, mobileText, false);
}

function CreateHeaderNavigationLink(
  url: string,
  target: string,
  linkText: string,
  mobileText?: string,
  allowTranslation = true
): HeaderNavigationLink {
  return {
    type: 'link',
    link: (
      <a className={allowTranslation ? '' : 'GlobalLinkNoTx'} href={url} target={target}>
        {linkText}
      </a>
    ),
    mobileLink: (
      <a className={allowTranslation ? '' : 'GlobalLinkNoTx'} href={url} target={target}>
        {mobileText ?? linkText}
      </a>
    ),
  } as HeaderNavigationLink;
}
